type paramsType = {
  slug: string
}

type paramsPricesType = paramsType & {
  province: string
  dateIn?: string
  dateOut?: string
  capacity?: string
  cos?: string
  rent_unit?: string
}

type paramsCottageDetail = paramsType & {
  province: string
  dateIn?: string
  dateOut?: string
  capacity?: string
}

type paramsCottageDetailFromCottageList = paramsType & {
  province: string
  d?: string
  c?: string
}

type paramsCottageOnlineBooking = paramsType & {
  province: string
  id: string
}

type paramsDeal = paramsType & {
  id: string
}

type paramsFilteredCottage = {
  url: string
  dateIn?: string
  dateOut?: string
  capacity?: string
}

type paramsCottageList = {
  l?: string
  q?: string
  d?: string
  c?: string
  f?: string
  min_price?: string
  max_price?: string
  ec?: string
  r?: string
  bo?: string
  sort?: string
  page?: string
}

/* OWNER ADMIN */
type paramsOwnerType = {
  ownerId: string
}
type paramsCottageType = paramsOwnerType & {
  cottageId: string
}
type paramsCatchOrderType = paramsCottageType & {
  catchOrderId: string
}
type paramsRentUnitType = paramsOwnerType & {
  rentUnitId: string
}
type paramsOwnerOnlineBookingView = paramsOwnerType & {
  onlineBookingId: string
}

type paramsOwnerMessagesByIdType = paramsOwnerType & {
  travelerId: string
}

/* TRAVELER ADMIN: */
type paramsTravelerType = {
  travelerId: string
}

type paramsTravelerTypeItem = paramsTravelerType & {
  itemId: string
}

type paramsTravelerTypeItemEdit = paramsTravelerType & {
  itemId: string
  addDomain?: boolean
}

type paramsTravelerMessagesListType = paramsTravelerType & {
  page?: number
}

type paramsTravelerMessagesByIdType = paramsTravelerType & {
  id: string
}

type paramsTravelerMessagesType = paramsTravelerType & {
  ownerId: string
}

/* OUTINGS */
type paramsOutingDetail = {
  outingId: string
}

type paramsOutingsList = {
  userId: string
}

type paramsOutingPlanner = {
  cottageUid?: string
}

/* PAYMENT FUNNEL */
type paramsFunnelType = paramsType & {
  province: string
  id: string
  message?: string
  error?: string
}

type paramsPaymentSuccess = paramsType & {
  province: string
  id: string
  slug: string
}

export default function () {
  const runtimeConfig = useRuntimeConfig()

  const DOMAIN = `${runtimeConfig.public.PROTOCOL}${runtimeConfig.public.DOMAIN_ES}`

  const DOMAIN_HELP = 'https://ayuda.escapadarural.com'

  const addDomain = (params: paramsType): string => {
    return `${DOMAIN}${params.slug}`
  }

  const deleteDomain = (url: URL): string => {
    return url.toString().substring(url.origin.length)
  }

  const home = (): string => {
    const rawURL = new URL('', DOMAIN)
    return deleteDomain(rawURL)
  }

  const holidaysList = (): string => {
    const rawURL = new URL('/vacaciones', DOMAIN)
    return deleteDomain(rawURL)
  }

  const holidaysDetail = (params: paramsType): string => {
    const rawURL = new URL(`/vacaciones/${params.slug}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const villagesList = (): string => {
    const rawURL = new URL('/pueblos', DOMAIN)
    return deleteDomain(rawURL)
  }

  const villagesDetail = (params: paramsType): string => {
    const rawURL = new URL(`/pueblos/${params.slug}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const escapadasList = (): string => {
    const rawURL = new URL('/escapadas', DOMAIN)
    return deleteDomain(rawURL)
  }

  const escapadasDetail = (params: paramsType): string => {
    const rawURL = new URL(`/escapadas/${params.slug}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const prices = (params: paramsPricesType): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/precios`,
      DOMAIN,
    )

    params.dateIn && rawURL.searchParams.set('dateIn', params.dateIn)
    params.dateOut && rawURL.searchParams.set('dateOut', params.dateOut)
    params.capacity && rawURL.searchParams.set('capacity', params.capacity)
    params.cos && rawURL.searchParams.set('cos', params.cos)
    params.rent_unit && rawURL.searchParams.set('rent_unit', params.rent_unit)

    return deleteDomain(rawURL)
  }

  const dealsList = (): string => {
    const rawURL = new URL('/ofertas-casas-rurales', DOMAIN)
    return deleteDomain(rawURL)
  }

  const dealDetail = (params: paramsDeal): string => {
    const rawURL = new URL(`/oferta/${params.slug}/${params.id}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const cottageDetail = (
    params: paramsCottageDetail,
    withDomain?: boolean,
  ): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}`,
      DOMAIN,
    )

    params.dateIn && rawURL.searchParams.set('dateIn', params.dateIn)
    params.dateOut && rawURL.searchParams.set('dateOut', params.dateOut)
    params.capacity && rawURL.searchParams.set('capacity', params.capacity)

    return withDomain ? rawURL.toString() : deleteDomain(rawURL)
  }

  const cottageDetailFromCottageList = (
    params: paramsCottageDetailFromCottageList,
    withDomain?: boolean,
  ): string => {
    let dateIn: string | undefined
    let dateOut: string | undefined

    if (params.d) {
      const dates = params.d.split(',')
      if (dates.length === 2) {
        dateIn = dates[0]
        dateOut = dates[1]
      }
    }

    return cottageDetail(
      {
        slug: params.slug,
        province: params.province,
        dateIn,
        dateOut,
        capacity: params.c,
      },
      withDomain,
    )
  }

  const cottageContact = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/contacto-email`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottageContactWhatsApp = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/contacto-whatsapp`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottageContactPrebooking = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/pre-reserva`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottageContactOnlineBooking = (
    params: paramsCottageOnlineBooking,
  ): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/online-booking/${params.id}`,
      DOMAIN,
    )

    return deleteDomain(rawURL)
  }

  const cottageContactOnlineBookingNew = (
    params: paramsCottageOnlineBooking,
  ): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/online-booking-new/${params.id}`,
      DOMAIN,
    )

    return deleteDomain(rawURL)
  }

  const cottagePhones = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/private/casa-rural/${params.province}/${params.slug}/telefono`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const allCottageReviews = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/opiniones`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const cottageReviewRate = (params: paramsCottageDetail): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/crear-opinion`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const filteredCottage = (params: paramsFilteredCottage): string => {
    const rawURL = new URL(params.url, DOMAIN)

    params.dateIn && rawURL.searchParams.set('dateIn', params.dateIn)
    params.dateOut && rawURL.searchParams.set('dateOut', params.dateOut)
    params.capacity && rawURL.searchParams.set('capacity', params.capacity)

    return deleteDomain(rawURL)
  }

  const cottageList = (params?: paramsCottageList): string => {
    const rawURL = new URL('/resultados', DOMAIN)

    if (params?.l) rawURL.searchParams.set('l', params.l)
    if (params?.q) rawURL.searchParams.set('q', params.q)
    if (params?.d) rawURL.searchParams.set('d', params.d)
    if (params?.c) rawURL.searchParams.set('c', params.c)
    if (params?.f) rawURL.searchParams.set('f', params.f)
    if (params?.min_price)
      rawURL.searchParams.set('min_price', params.min_price)
    if (params?.max_price)
      rawURL.searchParams.set('max_price', params.max_price)
    if (params?.ec) rawURL.searchParams.set('ec', params.ec)
    if (params?.r) rawURL.searchParams.set('r', params.r)
    if (params?.bo) rawURL.searchParams.set('bo', params.bo)
    if (params?.sort) rawURL.searchParams.set('sort', params.sort)
    if (params?.page) rawURL.searchParams.set('page', params.page)

    return deleteDomain(rawURL)
  }

  const cottageListMap = (params?: paramsCottageList): string => {
    const rawURL = new URL('/resultados/mapa', DOMAIN)

    if (params?.l) rawURL.searchParams.set('l', params.l)
    if (params?.q) rawURL.searchParams.set('q', params.q)
    if (params?.d) rawURL.searchParams.set('d', params.d)
    if (params?.c) rawURL.searchParams.set('c', params.c)
    if (params?.f) rawURL.searchParams.set('f', params.f)
    if (params?.min_price)
      rawURL.searchParams.set('min_price', params.min_price)
    if (params?.max_price)
      rawURL.searchParams.set('max_price', params.max_price)
    if (params?.ec) rawURL.searchParams.set('ec', params.ec)
    if (params?.r) rawURL.searchParams.set('r', params.r)
    if (params?.bo) rawURL.searchParams.set('bo', params.bo)
    if (params?.sort) rawURL.searchParams.set('sort', params.sort)
    if (params?.page) rawURL.searchParams.set('page', params.page)

    return deleteDomain(rawURL)
  }

  /* TODO: Deprecated in favor of cottageDefinedList */
  const cottageProvince = (params: paramsType): string => {
    const rawURL = new URL(`/casas-rurales/${params.slug}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const cottageDefinedList = (params?: paramsType): string => {
    if (params) {
      const rawURL = new URL(`/casas-rurales/${params.slug}`, DOMAIN)
      return deleteDomain(rawURL)
    } else {
      const rawURL = new URL('/casas-rurales', DOMAIN)
      return deleteDomain(rawURL)
    }
  }

  const mostWantedList = (): string => {
    const rawURL = new URL('/lo-mas-buscado', DOMAIN)
    return deleteDomain(rawURL)
  }

  const magazine = (): string => {
    const rawURL = new URL('/blog', DOMAIN)
    return deleteDomain(rawURL)
  }

  const help = (): string => {
    const rawURL = new URL('/support/tickets/new', DOMAIN_HELP)
    return rawURL.href
  }

  const helpFormReportContent = (): string => {
    const rawURL = new URL(
      '/support/tickets/new?ticket_form=denunciar_contenido_il%C3%ADcito',
      DOMAIN_HELP,
    )
    return rawURL.href
  }

  const helpArticle = (): string => {
    const rawURL = new URL(
      '/support/solutions/articles/205000048076-informaci%C3%B3n-sobre-la-normativa-de-la-uni%C3%B3n-europea-dac7',
      DOMAIN_HELP,
    )
    return rawURL.href
  }

  const helpArticleCalendarSync = (): string => {
    const rawURL = new URL(
      '/support/solutions/articles/205000048035-vincular-ocupaci%C3%B3n-con-multi-ical',
      DOMAIN_HELP,
    )
    return rawURL.href
  }

  /* TRAVELER ADMIN: */
  const travelerBooking = (params: paramsTravelerTypeItem): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/online-booking/view/${params.itemId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const travelerBookingEdit = (params: paramsTravelerTypeItemEdit): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/online-booking/edit/${params.itemId}`,
      DOMAIN,
    )

    if (params.addDomain === true) {
      return rawURL.toString()
    } else {
      return deleteDomain(rawURL)
    }
  }

  const travelerBookingCancel = (params: paramsTravelerTypeItem): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/online-booking/cancel/${params.itemId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const travelerBookingGuests = (params: paramsTravelerTypeItem): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/online-booking/guests/${params.itemId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const travelerMessagesList = (
    params: paramsTravelerMessagesListType,
  ): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/menu/messages`,
      DOMAIN,
    )

    if (params.page) rawURL.searchParams.set('page', params.page.toString())
    return deleteDomain(rawURL)
  }

  const travelerMessagesById = (
    params: paramsTravelerMessagesByIdType,
  ): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/menu/conversation/${params.id}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const travelerMessages = (params: paramsTravelerMessagesType): string => {
    const rawURL = new URL(
      `/traveler/${params.travelerId}/menu/conversation/${params.ownerId}${params.travelerId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const travelerFavorites = (params: paramsTravelerType): string => {
    const rawURL = new URL(`/viajero/${params.travelerId}/listas`, DOMAIN)
    return deleteDomain(rawURL)
  }

  /* OUTINGS */
  const outingDetail = (params: paramsOutingDetail): string => {
    const rawURL = new URL(`traveler/outings/detail/${params.outingId}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const outingsList = (params: paramsOutingsList): string => {
    const rawURL = new URL(`traveler/outings/list/${params.userId}`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const outingPlanner = (params?: paramsOutingPlanner): string => {
    const rawURL = new URL('traveler/outings/planifica-tu-escapada', DOMAIN)
    if (params?.cottageUid) {
      rawURL.searchParams.set('cottageUid', params.cottageUid)
    }
    return deleteDomain(rawURL)
  }

  /* OWNER ADMIN */
  const ownerNewCottage = (params: paramsOwnerType): string => {
    const rawURL = new URL(`/propietario/${params.ownerId}/casa/nueva`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const moreInfoDsa = (): string => {
    const rawURL = new URL(
      '/support/solutions/articles/205000048054-informaci%C3%B3n-sobre-el-reglamento-de-servicios-digitales',
      DOMAIN_HELP,
    )
    return rawURL.href
  }

  const onlineBookingConfig = (params: paramsOwnerType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/online-booking/config`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCalendarIcal = (params: paramsRentUnitType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/calendar/sync/${params.rentUnitId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCalendarIcalNew = (params: paramsRentUnitType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/calendar/sync/${params.rentUnitId}/new`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCalendarSync = (params: paramsOwnerType): string => {
    const rawURL = new URL(`/owner/${params.ownerId}/calendar/sync`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const ownerCalendarAvaibook = (params: paramsOwnerType): string => {
    const rawURL = new URL(`/owner/${params.ownerId}/calendar/avaibook`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const ownerCalendarMisterplan = (params: paramsOwnerType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/calendar/misterplan`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerMessages = (params: paramsOwnerMessagesByIdType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/menu/conversation/${params.ownerId}${params.travelerId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const useConditions = (): string => {
    const rawURL = new URL('/aviso-legal', DOMAIN)
    return deleteDomain(rawURL)
  }

  const privacyPolicy = (): string => {
    const rawURL = new URL('/politica-privacidad', DOMAIN)
    return deleteDomain(rawURL)
  }

  const photosUpload = (params: paramsCottageType): string => {
    const rawURL = new URL(
      `/propietario/${params.ownerId}/casa/${params.cottageId}/upload/fotos`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCottageDetail = (params: paramsCottageType): string => {
    const rawURL = new URL(
      `/propietario/${params.ownerId}/alojamiento/${params.cottageId}/editar/datos-basicos`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCottageSelectService = (params: paramsCottageType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/cottage/${params.cottageId}/select-service`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCottageNew = (params: paramsOwnerType): string => {
    const rawURL = new URL(`/owner/${params.ownerId}/cottage/new`, DOMAIN)
    return deleteDomain(rawURL)
  }

  const ownerCottageCatchOrderPayment = (
    params: paramsCatchOrderType,
  ): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/cottage/${params.cottageId}/catchorder/${params.catchOrderId}/payment`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const catchOrderPaymentSuccess = (params: paramsCatchOrderType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/cottage/${params.cottageId}/catchorder/${params.catchOrderId}/payment/success`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const rememberPassword = (): string => {
    const rawURL = new URL('/viajero/recordar-password', DOMAIN)
    return deleteDomain(rawURL)
  }

  /* PAYMENT FUNNEL */
  const paymentFunnel = (params: paramsFunnelType): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/online-booking/${params.id}`,
      DOMAIN,
    )

    if (params.message) rawURL.searchParams.set('message', params.message)
    if (params.error) rawURL.searchParams.set('error', params.error)

    return rawURL.toString()
  }

  const paymentSuccess = (params: paramsPaymentSuccess): string => {
    const rawURL = new URL(
      `/casa-rural/${params.province}/${params.slug}/solicitud/online-booking/${params.id}/payment-success`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  /* AUTH  */
  const login = (): string => {
    const rawURL = new URL('/login', DOMAIN)
    return deleteDomain(rawURL)
  }

  const loginApp = (): string => {
    const rawURL = new URL('/login-app', DOMAIN)
    return deleteDomain(rawURL)
  }

  const travelerRegistration = (): string => {
    const rawURL = new URL('/viajero/registro', DOMAIN)
    return deleteDomain(rawURL)
  }

  const ownerRegistration = (): string => {
    const rawURL = new URL('/propietario/casa/nueva', DOMAIN)
    return deleteDomain(rawURL)
  }

  const ownerRegistrationAsTraveler = (): string => {
    const rawURL = new URL('/propietario/viajero-a-propietario', DOMAIN)
    return deleteDomain(rawURL)
  }

  const calendarQuickRentUnit = (params: paramsOwnerType): string => {
    return `${DOMAIN}/owner/${params.ownerId}/calendar-quick/rent-unit`
  }

  const ownerQualityRate = (params: paramsCottageType): string => {
    const rawURL = new URL(`/owner/${params.ownerId}/quality-rate`, DOMAIN)

    rawURL.searchParams.set('cottageId', params.cottageId)

    return deleteDomain(rawURL)
  }

  const ownerOnlineBookingView = (
    params: paramsOwnerOnlineBookingView,
  ): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/online-booking/view/${params.onlineBookingId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerOnlineBookingReject = (
    params: paramsOwnerOnlineBookingView,
  ): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/online-booking/reject/${params.onlineBookingId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerOnlineBookingCancel = (
    params: paramsOwnerOnlineBookingView,
  ): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/online-booking/cancel/${params.onlineBookingId}`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCalendarPlatforms = (params: paramsOwnerType): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/calendar/platforms`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  const ownerCottageSelectServiceLegalInfo = (
    params: paramsCatchOrderType,
  ): string => {
    const rawURL = new URL(
      `/owner/${params.ownerId}/cottage/${params.cottageId}/catchorder/${params.catchOrderId}/legal-info`,
      DOMAIN,
    )
    return deleteDomain(rawURL)
  }

  return {
    addDomain,
    deleteDomain,
    home,
    holidaysList,
    holidaysDetail,
    villagesList,
    villagesDetail,
    escapadasList,
    escapadasDetail,
    prices,
    dealsList,
    dealDetail,
    cottageDetail,
    cottageDetailFromCottageList,
    allCottageReviews,
    cottageReviewRate,
    filteredCottage,
    cottageList,
    cottageListMap,
    cottageProvince,
    cottageDefinedList,
    ownerNewCottage,
    ownerMessages,
    mostWantedList,
    magazine,
    help,
    helpFormReportContent,
    helpArticle,
    helpArticleCalendarSync,
    travelerBooking,
    travelerBookingEdit,
    travelerBookingCancel,
    travelerBookingGuests,
    travelerMessagesList,
    travelerMessagesById,
    travelerMessages,
    travelerFavorites,
    outingDetail,
    outingsList,
    outingPlanner,
    useConditions,
    privacyPolicy,
    photosUpload,
    ownerCottageDetail,
    ownerCottageSelectService,
    ownerCottageNew,
    ownerCottageCatchOrderPayment,
    catchOrderPaymentSuccess,
    rememberPassword,
    paymentFunnel,
    paymentSuccess,
    moreInfoDsa,
    onlineBookingConfig,
    ownerCalendarIcal,
    ownerCalendarIcalNew,
    ownerCalendarSync,
    ownerCalendarAvaibook,
    ownerCalendarMisterplan,
    login,
    loginApp,
    travelerRegistration,
    ownerRegistration,
    ownerRegistrationAsTraveler,
    cottageContact,
    cottageContactWhatsApp,
    cottageContactPrebooking,
    cottageContactOnlineBooking,
    cottageContactOnlineBookingNew,
    cottagePhones,
    calendarQuickRentUnit,
    ownerQualityRate,
    ownerOnlineBookingView,
    ownerOnlineBookingReject,
    ownerOnlineBookingCancel,
    ownerCalendarPlatforms,
    ownerCottageSelectServiceLegalInfo,
  }
}
